import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import blackLogo from '../assets/LOGO BLACK.png';
import { API_CONFIG, QUICK_SIGNUP_STORAGE_KEY } from '../config/envConfig';
import {
  completeBuyNowSignup,
  looksLikeWebsite,
  toCompanyWebsiteUrl,
} from '../utils/quickSignupAuth';
import { confirmCheckoutPayment } from '../utils/razorpayCheckout';

const REDIRECT_DELAY_SEC = 5;
const RETRY_DELAY_MS = 2000;
const MAX_RETRIES = 20;

const STYLES = `
.wo-pay-success{
  --ink:#0F172A;
  --muted:#64748B;
  min-height:100vh;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:32px 20px;
  background:linear-gradient(180deg,#E8F1FF 0%,#F5F8FF 42%,#FFFFFF 100%);
  color:var(--ink);
  font-family:'Instrument Sans',sans-serif;
  line-height:1.5;
}
.wo-pay-success *,.wo-pay-success *::before,.wo-pay-success *::after{box-sizing:border-box;margin:0;padding:0}
.wo-pay-success a{color:inherit;text-decoration:none}
.wo-pay-success-card{
  width:100%;
  max-width:560px;
  background:#fff;
  border-radius:20px;
  padding:28px 36px 40px;
  box-shadow:0 18px 50px rgba(15,23,42,.08);
  text-align:center;
}
.wo-pay-success-logo-wrap{
  display:flex;
  justify-content:flex-start;
  margin-bottom:28px;
}
.wo-pay-success-logo{height:28px;width:auto;display:block}
.wo-pay-success-check{
  width:72px;height:72px;margin:0 auto 22px;
  border-radius:50%;
  background:#22C55E;
  color:#fff;
  display:flex;align-items:center;justify-content:center;
  box-shadow:0 10px 24px rgba(34,197,94,.28);
}
.wo-pay-success-check svg{width:34px;height:34px;display:block}
.wo-pay-success-card h1{
  font-size:clamp(1.7rem,3vw,2.1rem);
  font-weight:700;
  letter-spacing:-0.02em;
  margin-bottom:14px;
  color:var(--ink);
}
.wo-pay-success-copy{
  max-width:420px;
  margin:0 auto 28px;
}
.wo-pay-success-copy p{
  font-size:1rem;
  color:var(--muted);
  margin:0 0 4px;
}
.wo-pay-success-countdown{
  font-size:.95rem;
  font-weight:600;
  color:var(--ink);
}
.wo-pay-success-error{
  margin-top:12px;
  font-size:.9rem;
  color:#C0392B;
}
.wo-pay-success-link{
  display:inline-block;
  margin-top:12px;
  color:#2563EB;
  font-size:.9rem;
  text-decoration:underline;
}
`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const readStoredPayment = () => {
  try {
    const raw = sessionStorage.getItem(QUICK_SIGNUP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.flow === 'payment' ? parsed : null;
  } catch {
    return null;
  }
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12.5l5 5L19 7" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState(REDIRECT_DELAY_SEC);
  const [error, setError] = useState('');
  const payloadRef = useRef(null);
  const timerDoneRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let countdownTimer = null;

    const fail = (message) => {
      if (cancelled) return;
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
      setError(message);
    };

    const tryRedirect = () => {
      if (cancelled || !timerDoneRef.current || !payloadRef.current) return;
      const payload = payloadRef.current;

      if (looksLikeWebsite(payload.suggested_website)) {
        completeBuyNowSignup({
          email: payload.email,
          otp: payload.otp,
          companyWebsite: toCompanyWebsiteUrl(payload.suggested_website),
        }).catch((err) => {
          fail(err.message || 'Unable to complete signup. Please try again.');
        });
        return;
      }

      navigate('/onboarding', { replace: true, state: payload });
    };

    const stored = location.state?.flow === 'payment' ? location.state : readStoredPayment();
    const params = new URLSearchParams(location.search);
    const paymentLinkId = params.get('razorpay_payment_link_id') || '';
    const subscriptionId = params.get('razorpay_subscription_id') || '';
    const paymentId = params.get('razorpay_payment_id') || '';
    const checkoutPending = Boolean(stored?.confirming && stored?.orderId && stored?.paymentResponse);
    const checkoutReady = Boolean(!stored?.confirming && stored?.email && stored?.otp);

    if (!checkoutPending && !checkoutReady && !paymentLinkId && !subscriptionId && !paymentId) {
      setError('Missing payment reference. Please return to pricing and try again.');
      return undefined;
    }

    let remaining = REDIRECT_DELAY_SEC;
    setCountdown(remaining);
    countdownTimer = setInterval(() => {
      remaining -= 1;
      if (cancelled) return;
      setCountdown(Math.max(remaining, 0));
      if (remaining <= 0) {
        clearInterval(countdownTimer);
        countdownTimer = null;
        timerDoneRef.current = true;
        tryRedirect();
      }
    }, 1000);

    (async () => {
      try {
        let payload = null;

        if (checkoutReady) {
          payload = {
            flow: 'payment',
            email: stored.email,
            otp: stored.otp,
            suggested_website: stored.suggested_website || '',
          };
        } else if (checkoutPending) {
          const otpData = await confirmCheckoutPayment(stored.orderId, stored.paymentResponse);
          if (cancelled) return;
          payload = {
            flow: 'payment',
            email: otpData.email,
            otp: otpData.otp,
            suggested_website: stored.suggested_website || '',
          };
        } else {
          const tokenPayload = paymentLinkId
            ? { razorpay_payment_link_id: paymentLinkId }
            : subscriptionId
              ? { razorpay_subscription_id: subscriptionId }
              : { razorpay_payment_id: paymentId };

          let data = null;
          for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
            if (cancelled) return;

            const response = await fetch(API_CONFIG.paymentSuccessTokenUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(tokenPayload),
            });
            data = await response.json().catch(() => ({}));

            if (response.status === 402) {
              await sleep(RETRY_DELAY_MS);
              continue;
            }

            if (!response.ok || data.error) {
              throw new Error(data.error || data.message || 'Unable to confirm payment. Please try again.');
            }

            if (data.email && data.otp) {
              break;
            }

            if (data.success === false) {
              await sleep(RETRY_DELAY_MS);
              continue;
            }

            throw new Error(data.message || 'Unable to confirm payment. Please try again.');
          }

          if (!data?.email || !data?.otp) {
            throw new Error('Payment confirmation timed out. Please try again in a moment.');
          }

          payload = {
            flow: 'payment',
            email: data.email,
            otp: data.otp,
            suggested_website: data.suggested_website || stored?.suggested_website || '',
          };
        }

        if (cancelled) return;

        sessionStorage.setItem(QUICK_SIGNUP_STORAGE_KEY, JSON.stringify(payload));
        payloadRef.current = payload;
        tryRedirect();
      } catch (err) {
        fail(err.message || 'Something went wrong. Please try again.');
      }
    })();

    return () => {
      cancelled = true;
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [location.search, location.state, navigate]);

  return (
    <div className="wo-pay-success">
      <style>{STYLES}</style>
      <div className="wo-pay-success-card">
        <div className="wo-pay-success-logo-wrap">
          <Link to="/" aria-label="WhiteOwl home">
            <img src={blackLogo} alt="WhiteOwl" className="wo-pay-success-logo" />
          </Link>
        </div>

        <div className="wo-pay-success-check" aria-hidden="true">
          <CheckIcon />
        </div>

        <h1>Payment confirmed.</h1>
        <div className="wo-pay-success-copy">
          <p>Thank you for your purchase. Your subscription is now active.</p>
          <p>Please check your inbox for next steps and account setup details.</p>
        </div>

        {!error ? (
          <p className="wo-pay-success-countdown">
            Redirecting you to onboarding in {countdown} sec...
          </p>
        ) : (
          <>
            <p className="wo-pay-success-error">{error}</p>
            <Link to="/#pricing" className="wo-pay-success-link">Back to pricing</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
